import React, { useEffect, useRef, useState } from 'react';
import {
  AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader,
  AlertDialogBody, AlertDialogFooter,
  Text, Button, Spinner, Flex, Box, VStack, SimpleGrid, Stat, StatLabel, StatNumber,
  Alert, AlertIcon, useColorModeValue, useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useOrgDeleteImpact, useRequestOrgDelete, useOrgDeleteStatus } from '../../../api/orgAdminApi';
import InputField from '../../../components/fields/InputField';

interface OrgDeleteDialogProps {
  orgId: string;
  orgName: string;
  isOpen: boolean;
  onClose: () => void;
}

export function OrgDeleteDialog({ orgId, orgName, isOpen, onClose }: OrgDeleteDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const textColor = useColorModeValue('navy.700', 'white');
  const dialogBg = useColorModeValue('white', 'navy.800');
  const statBg = useColorModeValue('secondaryGray.300', 'navy.700');

  const { data: impact, isLoading } = useOrgDeleteImpact(orgId, { enabled: isOpen && !!orgId });
  const requestDelete = useRequestOrgDelete();

  // Poll the deletion status once the purge has been kicked off. Keep polling even when the
  // browser tab is backgrounded — otherwise React Query pauses the interval on blur (default
  // refetchIntervalInBackground=false) and the dialog never sees the (server-side) job finish,
  // so it appears stuck open until the admin returns to the tab.
  const { data: status } = useOrgDeleteStatus(orgId, {
    enabled: deleting,
    refetchIntervalInBackground: true,
    refetchInterval: (query) => {
      // Poll until a terminal state. Checking "not terminal" (rather than matching specific
      // in-progress strings) keeps polling through the initial undefined and is robust to the
      // exact status spelling the API returns.
      const s = query.state.data?.status;
      return s === 'completed' || s === 'failed' ? false : 1500;
    },
  });

  useEffect(() => {
    if (!deleting || !status) return;
    if (status.status === 'completed') {
      setDeleting(false);
      toast({ title: t('admin.organizations.delete.successToast', { name: orgName }), status: 'success', duration: 4000 });
      queryClient.invalidateQueries({ queryKey: ['admin-organizations'] });
      onClose();
      navigate('/organizations');
    } else if (status.status === 'failed') {
      setDeleting(false);
      toast({ title: t('admin.organizations.delete.errorToast'), status: 'error', duration: 6000 });
    }
  }, [deleting, status, navigate, onClose, orgName, queryClient, t, toast]);

  const isConfirmMatch = confirmText.trim() === orgName;
  const busy = requestDelete.isPending || deleting;

  const handleConfirm = () => {
    requestDelete.mutate(
      { orgId, confirmName: confirmText.trim() },
      {
        onSuccess: () => {
          // Drop any prior job's cached status so polling tracks the NEW job (avoids a stale
          // 'failed'/'completed' from a previous attempt being served within staleTime).
          queryClient.removeQueries({ queryKey: ['admin-org-delete-status', orgId] });
          setDeleting(true);
        },
        onError: () => toast({ title: t('admin.organizations.delete.errorToast'), status: 'error', duration: 6000 }),
      },
    );
  };

  const handleClose = () => {
    if (busy) return;
    setConfirmText('');
    onClose();
  };

  const phaseLabel = status?.phase
    ? t(`admin.organizations.delete.phase.${status.phase}`, { defaultValue: status.phase })
    : '';

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={handleClose} isCentered>
      <AlertDialogOverlay />
      <AlertDialogContent data-testid="org-delete-dialog" maxW="560px" borderRadius="20px" bg={dialogBg}>
        <AlertDialogHeader pb="8px">
          <Text fontSize="lg" fontWeight="700" color={textColor}>
            {t('admin.organizations.delete.dialogTitle')}
          </Text>
        </AlertDialogHeader>

        <AlertDialogBody>
          <VStack spacing={4} align="stretch">
            <Alert status="error" borderRadius="16px">
              <AlertIcon />
              <Text fontSize="sm">{t('admin.organizations.delete.warning')}</Text>
            </Alert>

            {isLoading ? (
              <Flex justify="center" p={4}><Spinner size="md" /></Flex>
            ) : impact ? (
              <>
                {impact.alreadyDeleting && (
                  <Alert status="warning" borderRadius="16px" data-testid="org-delete-already">
                    <AlertIcon />
                    <Text fontSize="sm">{t('admin.organizations.delete.alreadyDeleting')}</Text>
                  </Alert>
                )}

                <SimpleGrid columns={3} spacing={3} data-testid="org-delete-impact">
                  {([
                    ['locations', impact.locations],
                    ['members', impact.members],
                    ['playlists', impact.playlists],
                    ['scenarios', impact.scenarios],
                    ['tracks', impact.tracks],
                  ] as const).map(([key, value]) => (
                    <Stat key={key} bg={statBg} borderRadius="12px" p="10px 12px">
                      <StatLabel fontSize="xs">{t(`admin.organizations.delete.counts.${key}`)}</StatLabel>
                      <StatNumber fontSize="lg">{value}</StatNumber>
                    </Stat>
                  ))}
                </SimpleGrid>

                <Box>
                  <Text fontSize="sm" color={textColor}>
                    {t('admin.organizations.delete.ownerLine', { email: impact.ownerEmail ?? '—' })}
                  </Text>
                  {impact.subscriptionPlan && (
                    <Text fontSize="sm" color={textColor}>
                      {t('admin.organizations.delete.subscriptionLine', {
                        plan: impact.subscriptionPlan,
                        status: impact.subscriptionStatus ?? '',
                      })}
                    </Text>
                  )}
                </Box>

                {deleting ? (
                  <Flex align="center" gap={3} data-testid="org-delete-progress">
                    <Spinner size="sm" />
                    <Text fontSize="sm" color={textColor}>
                      {t('admin.organizations.delete.inProgress')} {phaseLabel}
                    </Text>
                  </Flex>
                ) : (
                  <Box>
                    <Text fontSize="sm" color={textColor} mb={1}>
                      {t('admin.organizations.delete.confirmPrompt')}
                    </Text>
                    <Text fontSize="sm" fontWeight="700" color={textColor} mb={2}>{orgName}</Text>
                    <InputField
                      id="org-delete-confirm-input"
                      data-testid="org-delete-confirm-input"
                      value={confirmText}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmText(e.target.value)}
                      placeholder={orgName}
                      isDisabled={busy || impact.alreadyDeleting}
                      mb="0"
                    />
                  </Box>
                )}
              </>
            ) : null}
          </VStack>
        </AlertDialogBody>

        <AlertDialogFooter gap={2}>
          <Button ref={cancelRef} onClick={handleClose} isDisabled={busy} variant="outline" borderRadius="16px">
            {t('common.cancel')}
          </Button>
          <Button
            data-testid="org-delete-confirm-button"
            colorScheme="red"
            onClick={handleConfirm}
            isDisabled={!isConfirmMatch || busy || isLoading || impact?.alreadyDeleting}
            isLoading={busy}
            borderRadius="16px"
          >
            {t('admin.organizations.delete.confirmButton')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default OrgDeleteDialog;
